<template>
  <div>
    <button @click="$router.push('/lab/equipment')" class="flex items-center text-sm text-custom-blue mb-5 hover:underline">
      <svg class="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/>
      </svg>
      Back to Equipment Register
    </button>

    <div v-if="loading" class="flex justify-center py-16">
      <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-custom-yellow"></div>
    </div>

    <template v-else-if="equipment">
      <!-- Info card -->
      <div class="mb-6 p-5 bg-white border border-gray-200 rounded-lg shadow-sm">
        <div class="flex items-center justify-between mb-3">
          <h1 class="text-xl font-bold text-custom-blue">{{ equipment.name }}</h1>
          <span class="px-3 py-1 rounded-full text-sm font-medium" :class="statusClass(equipment.status)">
            {{ statusLabel(equipment.status) }}
          </span>
        </div>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-700">
          <div><span class="text-xs text-gray-400 block">Type</span>{{ label(equipment.equipment_type) }}</div>
          <div><span class="text-xs text-gray-400 block">Make / Model</span>{{ [equipment.make, equipment.model].filter(Boolean).join(' / ') || '—' }}</div>
          <div><span class="text-xs text-gray-400 block">Serial No.</span>{{ equipment.serial_number || '—' }}</div>
          <div><span class="text-xs text-gray-400 block">Location</span>{{ equipment.location || '—' }}</div>
          <div><span class="text-xs text-gray-400 block">Acquired</span>{{ equipment.acquisition_date || '—' }}</div>
          <div><span class="text-xs text-gray-400 block">Warranty Expiry</span>{{ equipment.warranty_expiry || '—' }}</div>
          <div v-if="equipment.notes" class="md:col-span-2"><span class="text-xs text-gray-400 block">Notes</span>{{ equipment.notes }}</div>
        </div>
      </div>

      <!-- Last maintenance / calibration summary -->
      <div class="grid grid-cols-2 gap-4 mb-6">
        <div v-for="kind in ['maintenance','calibration']" :key="kind"
          class="p-4 bg-white border border-gray-200 rounded-lg">
          <p class="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
            Last {{ kind.charAt(0).toUpperCase() + kind.slice(1) }}
          </p>
          <template v-if="equipment[`last_${kind}`]">
            <p class="text-sm font-semibold text-gray-800">{{ equipment[`last_${kind}`].performed_at }}</p>
            <p class="text-xs text-gray-500 mt-0.5">
              Next due: <span class="font-medium">{{ equipment[`last_${kind}`].next_due_date || '—' }}</span>
            </p>
            <span v-if="equipment[`last_${kind}`].outcome"
              class="mt-1 inline-block px-2 py-0.5 rounded-full text-xs font-medium"
              :class="outcomeClass(equipment[`last_${kind}`].outcome)">
              {{ equipment[`last_${kind}`].outcome }}
            </span>
          </template>
          <p v-else class="text-sm text-gray-400">No record yet</p>
        </div>
      </div>

      <!-- Log entry form toggle -->
      <div class="flex justify-between items-center mb-3">
        <h2 class="text-sm font-semibold text-gray-600">Maintenance Log ({{ logs.length }})</h2>
        <button @click="showForm = !showForm"
          class="px-4 py-2 text-sm bg-custom-yellow text-white rounded hover:bg-orange-600">
          {{ showForm ? 'Cancel' : '+ Add Log Entry' }}
        </button>
      </div>

      <!-- Inline log form -->
      <div v-if="showForm" class="mb-5 p-5 border border-gray-200 rounded-lg bg-gray-50">
        <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">Log Type *</label>
            <select v-model="form.log_type" class="w-full border border-gray-300 rounded p-2 text-sm bg-white">
              <option value="">— select —</option>
              <option value="maintenance">Maintenance</option>
              <option value="calibration">Calibration</option>
              <option value="repair">Repair</option>
              <option value="inspection">Inspection</option>
            </select>
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">Performed By</label>
            <input v-model="form.performed_by" type="text" placeholder="Engineer / technician name"
              class="w-full border border-gray-300 rounded p-2 text-sm bg-white" />
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">Date Performed *</label>
            <input v-model="form.performed_at" type="date"
              class="w-full border border-gray-300 rounded p-2 text-sm bg-white" />
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">Next Due Date</label>
            <input v-model="form.next_due_date" type="date"
              class="w-full border border-gray-300 rounded p-2 text-sm bg-white" />
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">Outcome</label>
            <select v-model="form.outcome" class="w-full border border-gray-300 rounded p-2 text-sm bg-white">
              <option value="">— none —</option>
              <option value="passed">Passed</option>
              <option value="failed">Failed</option>
              <option value="conditional">Conditional</option>
            </select>
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">Notes</label>
            <input v-model="form.notes" type="text" placeholder="Optional…"
              class="w-full border border-gray-300 rounded p-2 text-sm bg-white" />
          </div>
        </div>
        <div class="flex justify-end mt-4">
          <button @click="submitLog" :disabled="saving"
            class="px-5 py-2 text-sm bg-custom-yellow text-white rounded hover:bg-orange-600 disabled:opacity-50">
            {{ saving ? 'Saving…' : 'Record' }}
          </button>
        </div>
      </div>

      <!-- Log history table -->
      <div v-if="logsLoading" class="flex justify-center py-8">
        <div class="animate-spin rounded-full h-8 w-8 border-t-4 border-custom-yellow"></div>
      </div>
      <div v-else class="overflow-x-auto shadow-sm rounded-sm">
        <table class="w-full text-left text-gray-700 text-sm">
          <thead>
            <tr class="bg-gray-100">
              <th class="py-3 px-4">#</th>
              <th class="py-3 px-4">Date</th>
              <th class="py-3 px-4">Type</th>
              <th class="py-3 px-4">Performed By</th>
              <th class="py-3 px-4">Outcome</th>
              <th class="py-3 px-4">Next Due</th>
              <th class="py-3 px-4">Notes</th>
            </tr>
          </thead>
          <tbody class="font-light text-gray-600">
            <tr v-if="logs.length === 0">
              <td colspan="7" class="py-6 text-center text-gray-400">No log entries yet.</td>
            </tr>
            <tr v-for="(l, idx) in logs" :key="l.id" class="border-b border-gray-200 hover:bg-gray-50">
              <td class="py-3 px-4">{{ idx + 1 }}</td>
              <td class="py-3 px-4">{{ l.performed_at }}</td>
              <td class="py-3 px-4 capitalize">{{ l.log_type }}</td>
              <td class="py-3 px-4">{{ l.performed_by || '—' }}</td>
              <td class="py-3 px-4">
                <span v-if="l.outcome" class="px-2 py-0.5 rounded-full text-xs font-medium" :class="outcomeClass(l.outcome)">
                  {{ l.outcome }}
                </span>
                <span v-else class="text-gray-400">—</span>
              </td>
              <td class="py-3 px-4">{{ l.next_due_date || '—' }}</td>
              <td class="py-3 px-4 text-gray-400">{{ l.notes || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <div v-else class="py-16 text-center text-gray-400">Equipment not found.</div>
  </div>
</template>

<script>
import apiClient from '@/services/api'

export default {
  name: 'LabEquipmentDetailPage',
  data() {
    const today = new Date().toISOString().split('T')[0]
    return {
      equipment:   null,
      loading:     true,
      logs:        [],
      logsLoading: false,
      showForm:    false,
      saving:      false,
      form: { log_type: '', performed_by: '', performed_at: today, next_due_date: '', outcome: '', notes: '' },
    }
  },
  async mounted() {
    const id = this.$route.params.id
    await this.loadEquipment(id)
    await this.loadLogs(id)
  },
  methods: {
    async loadEquipment(id) {
      this.loading = true
      try {
        const res = await apiClient.listLabEquipment()
        if (res.success && Array.isArray(res.data))
          this.equipment = res.data.find(e => e.id === id) || null
      } catch (e) {
        console.error('Failed to load equipment', e)
      } finally {
        this.loading = false
      }
    },
    async loadLogs(id) {
      this.logsLoading = true
      try {
        const res = await apiClient.listEquipmentLogs(id)
        if (res.success && Array.isArray(res.data)) this.logs = res.data
      } catch (e) {
        console.error('Failed to load logs', e)
      } finally {
        this.logsLoading = false
      }
    },
    async submitLog() {
      if (!this.form.log_type || !this.form.performed_at) {
        this.$toast.error('Log type and date are required')
        return
      }
      this.saving = true
      try {
        const id  = this.$route.params.id
        const res = await apiClient.createEquipmentLog(id, this.form)
        if (res.success) {
          this.$toast.success('Log entry recorded')
          this.showForm = false
          this.form = { log_type: '', performed_by: '', performed_at: new Date().toISOString().split('T')[0], next_due_date: '', outcome: '', notes: '' }
          await this.loadEquipment(id)
          await this.loadLogs(id)
        } else {
          this.$toast.error(res.message || 'Failed to record log')
        }
      } catch {
        this.$toast.error('Failed to record log')
      } finally {
        this.saving = false
      }
    },
    label(type) {
      return type ? type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ') : '—'
    },
    statusLabel(s) {
      return { operational: 'Operational', under_maintenance: 'Under Maintenance', decommissioned: 'Decommissioned' }[s] || s
    },
    statusClass(s) {
      return { operational: 'bg-green-100 text-green-800', under_maintenance: 'bg-amber-100 text-amber-800', decommissioned: 'bg-gray-100 text-gray-500' }[s] || ''
    },
    outcomeClass(o) {
      return { passed: 'bg-green-100 text-green-800', failed: 'bg-red-100 text-red-800', conditional: 'bg-amber-100 text-amber-800' }[o] || ''
    },
  },
}
</script>
